import { Inject } from '@odinjs/odin';
import { ButtonAPI, FieldAPI, FormAPI, ToastNotificationAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { ContextType, Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnChange, OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { SubmittableFragmentContext } from '@philips-internal/oswald-ui-core/lib/component/context';
import { ChangeEvent, MountEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { HttpResponse } from '@philips-internal/oswald-ui-core/lib/services/http-service';
import { isMobile } from '@philips-internal/oswald-ui-utils';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { ToastResponse } from '../../interfaces/ToastResponse';
import { HttpServiceError, HttpServiceResponse, PatientHttpService } from '../../services/patient-http-service';
import { showToastNotificationMessage, showToastNotificationSuccess, showToastNotificationWarning } from '../../utils/toast-notification-utils';
import ChangePasswordCommonsController from '../commons/change-password-commons-controller';
import { ChangeExpiredPasswordAliases, FragmentNames } from '../../codegen';

interface Message {
  messages: string[];
}

@Controller({ component: ChangeExpiredPasswordAliases.RENEW_LAYOUT, fragment: FragmentNames.CHANGE_EXPIRED_PASSWORD })
class ChangeExpiredPasswordController extends ChangePasswordCommonsController {

  @Inject(ChangeExpiredPasswordAliases.OLD_PASSWORD_FIELD)
    fieldActualPassword!: FieldAPI;

  @Inject(ChangeExpiredPasswordAliases.NEW_PASSWORD_FIELD)
    fieldNewPassword!: FieldAPI;

  @Inject(ChangeExpiredPasswordAliases.CONFIRM_PASSWORD_FIELD)
    fieldNewConfirmPassword!: FieldAPI;

  @Inject(ChangeExpiredPasswordAliases.RENEW_FORM)
    formPassword!: FormAPI;

  @Inject(ChangeExpiredPasswordAliases.CHANGE_PASSWORD_SUBMIT_BUTTON)
    confirmBtn!: ButtonAPI;

  @Inject(ContextType.SUBMITTABLE_FRAGMENT)
    submittableFragmentContext!: SubmittableFragmentContext;

  @OnMount({ target: ChangeExpiredPasswordAliases.RENEW_FORM })
  onMountModalAltSenha(): void {
    super.onMountModalAltSenha(this.fieldActualPassword, this.fieldNewPassword, this.fieldNewConfirmPassword, this.formPassword);
  }

  @OnMount({ target: ChangeExpiredPasswordAliases.TOAST_NOTIFICATION_DESKTOP })
  handleToastDesktopLoading(event: MountEvent): void {
    const toastDesktopAPI = event.target as ToastNotificationAPI;
    const message = this.context.get('message') as string;
    if(isMobile() && !!message) {
      toastDesktopAPI.setVisible(false);
      showToastNotificationWarning(message, undefined, true);
      return;
    }
    if(isMobile() && !message) {
      toastDesktopAPI.setVisible(false);
      return;
    }
    toastDesktopAPI.setHighlight(false);
    toastDesktopAPI.setMessage(message);
    toastDesktopAPI.onClose.listen(() => toastDesktopAPI.setVisible(false));
  }

  @OnChange({ target: [ChangeExpiredPasswordAliases.RENEW_FORM] })
  async onChangeFormAltSenha({ value }: ChangeEvent<Record<string, Value>>): Promise<void> {
    this.confirmBtn.setEnabled(await super.isDataValid(this.fieldActualPassword, this.fieldNewPassword, this.fieldNewConfirmPassword, value));
  }

  @OnClick({ target: ChangeExpiredPasswordAliases.CHANGE_PASSWORD_SUBMIT_BUTTON })
  onClickAltSenha(): void {
    this.confirmBtn.setEnabled(false);
    this.formPassword.setDisabled(true);
    this.renewPassword();
  }

  renewPassword(): void {
    const body = this.formPassword.getRecord();
    body.login = this.context.get('username');
    PatientHttpService.post(APPLICATION_ENDPOINTS.POST_RENEW_PASSWORD, {}, { body }).then(resp => {
      this.handleSuccess(resp);
    }).catch(({ response }: HttpServiceError<ToastResponse>) => {
      this.handleFailure(response);
    });
  }

  handleSuccess(resp: HttpResponse<unknown>): void {
    const record = resp.body as Message;
    if(record && record.messages) {
      const message = MessageService.translate(record.messages[0]);
      showToastNotificationSuccess(message, undefined, undefined, 'patient.forget.password.request.success');
    }
    this.close(new SubmitEvent())
  }

  close(submittedEvent: SubmitEvent): void {
    const { record } = submittedEvent;
    this.submittableFragmentContext.submit(new SubmitEvent({ record }));
  }

  async handleFailure(resp?: HttpServiceResponse<ToastResponse>): Promise<void> {
    this.formPassword.setEditing(true);
    this.confirmBtn.setEnabled(await super.isDataValid(this.fieldActualPassword, this.fieldNewPassword, this.fieldNewConfirmPassword, this.formPassword.getRecord()));
    const cookieToastId = 'd6537482-d5a4-4c7e-954a-ea2fc090af93';
    ToastService.close(cookieToastId);

    if (resp) {
      showToastNotificationMessage(resp.body, true);
    }
  }
}

export default ChangeExpiredPasswordController;
